// src/features/locations/DecisionsActionsCard.jsx
//
// WHATS-AHEAD-PANE (Direction C): "What's Ahead" category-led flat list card
// for the 2×2 Overview grid (bottom-right).
//
// All item types (Lease key dates, Project milestones, Decisions) are merged
// into a single flat list, sorted soonest-first. Each row is led by a coloured
// category tag. Items more than 31 days overdue are filtered out before rendering.
//
// Props:
//   location         — full location object (location.projects[], etc.)
//   activeLease      — active lease object | null
//   score            — accepted for API compat; not used in this layout
//   locationProjects — optional array of projects with milestones attached;
//                      falls back to location.projects when omitted.
//   projectDecisions — optional array of raw decision objects from /api/projects/:id/decisions

import React, { useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import { getProjectMilestones } from "../../data/projectsClient.js";
import { log } from "../../utils/logger.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
const SIXTY_DAYS_MS  = 60 * 24 * 60 * 60 * 1000;
const TWO_YEARS_MS   = 2 * 365.25 * 24 * 60 * 60 * 1000;

// Fixed height for the scrollable list body. Prevents the card from growing
// or shrinking as item counts change. (LOCATION-CARD-HEIGHT-LOCK-V2 contract.)
const LIST_BODY_MIN_HEIGHT = 240;

// Hairline divider color — T.hair is the canonical token for this value.
const HAIR_COLOR = T.hair;

// Category tag visual config — placeholder palette; Corey tunes later.
//   Key Date  → blue  (T.blueSoft bg / T.blue fg)
//   Milestone → teal  (T.opexSecBg bg / T.opexSecFg fg)
//   Decision  → purple (T.purpleBg bg / T.purple fg)
const CATEGORY_STYLES = {
  keydate:   { label: "Key Date",  bg: T.blueSoft,  fg: T.blue      },
  milestone: { label: "Milestone", bg: T.opexSecBg, fg: T.opexSecFg },
  decision:  { label: "Decision",  bg: T.purpleBg,  fg: T.purple    },
};

// Human-readable labels for known critical_dates types.
// Only types listed here are surfaced from the critical_dates array;
// unrecognised types (e.g. "deposit_burndown", "other") are silently excluded.
const CD_TYPE_LABELS = {
  renewal_notice: "Renewal notice deadline",
  break_option:   "Break option",
  expiration:     "Lease expiry",
  escalation:     "Rent escalation",
  rent_review:    "Rent review",
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

// Sentinel end-date guard — 9999-xx-xx represents rolling/ongoing leases.
function isSentinel(dateStr) {
  return !dateStr || dateStr.startsWith("9999");
}

// Safely parse a date field that may be a date-only string (YYYY-MM-DD) or
// an ISO timestamp (YYYY-MM-DDTHH:MM:SS.sssZ).
//
// Both forms are parsed at *local* midnight to avoid the timezone-induced
// day-shift that `new Date("2026-12-31T00:00:00.000Z")` causes for users
// west of UTC (UTC midnight maps to the previous calendar day locally).
// When the string contains "T", we extract only the leading YYYY-MM-DD
// portion and anchor it to local midnight — identical to parseDateOnly().
// See WPR-DATE-SERIALISATION lesson — always check for "T" before parsing.
export function parseDateStr(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr !== "string") return null;
  if (!dateStr.includes("T")) {
    const parts = dateStr.split("-").map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return null;
    const [y, m, d] = parts;
    const dt = new Date(y, m - 1, d);
    return Number.isFinite(dt.getTime()) ? dt : null;
  }
  const dt = new Date(dateStr.slice(0, 10) + "T00:00:00");
  return Number.isFinite(dt.getTime()) ? dt : null;
}

// Overdue-window filter — returns true (include) when the item's date is not
// more than 31 days in the past. Future items and items ≤ 31 days overdue
// are included; items > 31 days overdue are excluded.
// Returns false for null / unparseable dates (safe to exclude).
export function isWithinOverdueWindow(dateStr) {
  if (!dateStr) return false;
  const d = parseDateStr(dateStr);
  if (!d) return false;
  const cutoff = new Date(Date.now() - 31 * 24 * 60 * 60 * 1000);
  return d.getTime() >= cutoff.getTime();
}

// Format a date as "Mon D, YYYY" (e.g. "May 31, 2037") — abbreviated month.
function formatSpecificDate(dateStr) {
  const d = parseDateStr(dateStr);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Format a date as "Mon YYYY" (e.g. "Jul 2026").
function formatMonthYear(dateStr) {
  const d = parseDateStr(dateStr);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// ─── Countdown pill (pure helper — exported for unit tests) ──────────────────
//
// countdownPill(dateValue, granularity) → { label, variant }
//
// dateValue:   JS Date | ISO string | null
// granularity: 'day' | 'month' (both use same bucket logic)
//
// Label buckets:
//   past            → "overdue"           variant: "overdue"
//   current month   → "now"               variant: "amber"
//   < ~60 days      → "N days"            variant: "amber"
//   60–90 days      → "N mo"              variant: "amber"
//   < 1 year        → "N mo"              variant: "neutral"
//   1 yr < 2 yrs    → "N yr M mo" / "N yr" variant: "neutral"
//   ≥ 2 yrs         → "N yrs"             variant: "green"
export function countdownPill(dateValue, granularity = "day") {
  if (!dateValue) return { label: "—", variant: "neutral" };

  let target;
  if (dateValue instanceof Date) {
    target = dateValue;
  } else {
    target = parseDateStr(String(dateValue));
  }
  if (!target) return { label: "—", variant: "neutral" };

  const now    = new Date();
  const diffMs = target.getTime() - now.getTime();

  // Current calendar month → "now"
  if (
    target.getFullYear() === now.getFullYear() &&
    target.getMonth()    === now.getMonth()
  ) {
    return { label: "now", variant: "amber" };
  }

  // Past (different month) → overdue
  if (diffMs < 0) return { label: "overdue", variant: "overdue" };

  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // < ~60 days → "N days" (amber)
  if (diffMs < SIXTY_DAYS_MS) {
    return { label: `${diffDays} days`, variant: "amber" };
  }

  const diffMonths = Math.round(diffMs / (1000 * 60 * 60 * 24 * 30.44));

  // 60–90 days → "N mo" (still amber)
  if (diffMs < NINETY_DAYS_MS) {
    return { label: `${diffMonths} mo`, variant: "amber" };
  }

  // < 1 year → "N mo" (neutral)
  if (diffMonths < 12) {
    return { label: `${diffMonths} mo`, variant: "neutral" };
  }

  // 1–2 years → "N yr M mo" (or "N yr" when M=0)
  const yrs = Math.floor(diffMonths / 12);
  const mo  = diffMonths % 12;
  if (diffMs < TWO_YEARS_MS) {
    const suffix = mo > 0 ? ` ${mo} mo` : "";
    return { label: `${yrs} yr${suffix}`, variant: "neutral" };
  }

  // ≥ 2 years → "N yrs" (green)
  return { label: `${yrs} yrs`, variant: "green" };
}

// ─── Data derivation (exported for unit tests) ────────────────────────────────

// Derive a sorted list of Key Date items from the active lease.
// Each item: { id, label, date (ISO string) }
// Sorted ascending (soonest first).
//
// leaseId is used as an ID prefix so that items from different leases have
// unique React keys when flatMapped together in deriveAllKeyDateItems.
export function deriveKeyDateItems(activeLease) {
  if (!activeLease) return [];

  const leaseId   = activeLease.id ?? "unknown";
  const items     = [];
  const seenTypes = new Set();

  // Commencement
  const startDate = activeLease.startDate ?? activeLease.start_date ?? null;
  if (startDate) {
    items.push({ id: `${leaseId}-kd-start`, label: "Lease commencement", date: startDate });
  }

  // Expiry — skip sentinel (9999) / rolling end dates
  const endDate = activeLease.endDate ?? activeLease.end_date ?? null;
  if (endDate && !isSentinel(endDate)) {
    items.push({ id: `${leaseId}-kd-expiry`, label: "Lease expiry", date: endDate });
    seenTypes.add("expiration");
  }

  // Renewal notice deadline — authoritative source first
  const nnd = activeLease.nextNoticeDeadline ?? activeLease.next_notice_deadline ?? null;
  if (nnd) {
    items.push({ id: `${leaseId}-kd-notice`, label: "Renewal notice deadline", date: nnd });
    seenTypes.add("renewal_notice");
  } else if (endDate && !isSentinel(endDate) && activeLease.renewalNoticeMonths) {
    // Legacy derivation: end date minus notice period in months
    const d = parseDateStr(endDate);
    if (d) {
      d.setMonth(d.getMonth() - Number(activeLease.renewalNoticeMonths));
      const iso = [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, "0"),
        String(d.getDate()).padStart(2, "0"),
      ].join("-");
      items.push({ id: `${leaseId}-kd-notice-derived`, label: "Renewal notice deadline", date: iso });
      seenTypes.add("renewal_notice");
    }
  }

  // Break option
  const breakDate = activeLease.breakDate ?? activeLease.break_date ?? null;
  if (breakDate) {
    items.push({ id: `${leaseId}-kd-break`, label: "Break option", date: breakDate });
    seenTypes.add("break_option");
  }

  // Critical dates — skip types already covered by lease-level fields above
  const cds = Array.isArray(activeLease.criticalDates)  ? activeLease.criticalDates
            : Array.isArray(activeLease.critical_dates) ? activeLease.critical_dates
            : [];
  for (const cd of cds) {
    if (seenTypes.has(cd.type)) continue;
    const label = CD_TYPE_LABELS[cd.type];
    if (!label) continue;
    const dateField = cd.dueDate ?? cd.due_date ?? null;
    if (!dateField) continue;
    items.push({ id: `${leaseId}-kd-cd-${cd.id ?? cd.type}`, label, date: dateField });
  }

  // Sort ascending: soonest first; unparseable dates go last
  items.sort((a, b) => {
    const da = parseDateStr(a.date);
    const db = parseDateStr(b.date);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });

  return items;
}

// Find the first active project attached to the location.
export function findActiveProject(location, locationProjects) {
  const projects = Array.isArray(locationProjects) ? locationProjects
                 : Array.isArray(location?.projects) ? location.projects
                 : [];
  return (
    projects.find((p) => {
      const s = p.statusOverall ?? p.status_overall ?? p.status ?? "";
      return s === "active" || s === "in_progress" || s === "open";
    }) ?? null
  );
}

// Derive the synthetic lease renewal decision item (~12 months before expiry).
export function deriveDecisionItems(activeLease, location) {
  const items = [];
  if (!activeLease) return items;

  const expiryDate = activeLease.endDate ?? activeLease.end_date ?? null;
  if (expiryDate && !isSentinel(expiryDate)) {
    const exp = parseDateStr(expiryDate);
    if (exp) {
      const decisionMs  = exp.getTime() - 12 * 30 * 24 * 60 * 60 * 1000;
      const target      = new Date(decisionMs).toISOString().slice(0, 10);
      const addrLabel   = location?.address ?? location?.market ?? "this";
      items.push({
        id:         "decision-lease-renewal",
        title:      `Renew vs renegotiate - ${addrLabel} lease`,
        owner:      "Real Estate",
        targetDate: target,
      });
    }
  }

  return items;
}

// Normalise a raw decision record to the internal display shape.
export function normalizeProjectDecision(d) {
  if (!d || typeof d !== "object") return null;
  return {
    id:         d.id ?? `proj-dec-${d.title ?? "unknown"}`,
    title:      d.title ?? "(untitled decision)",
    owner:      d.owner ?? null,
    targetDate: d.neededByDate ?? d.needed_by_date ?? null,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CountdownPillEl({ dateStr, granularity }) {
  const { label, variant } = countdownPill(dateStr, granularity);
  if (label === "—") return null;

  let bg, fg, fw;
  if (variant === "overdue") {
    bg = T.pillRedBg;   fg = T.red;         fw = T.fwBold;
  } else if (variant === "amber") {
    bg = T.pillAmberBg; fg = T.amberDark;   fw = T.fwBold;
  } else if (variant === "green") {
    bg = T.pillGreenBg; fg = T.forestGreen; fw = T.fwSemibold;
  } else {
    bg = T.lightGray;   fg = T.mid;         fw = T.fwSemibold;
  }

  return (
    <span
      data-testid="kdm-countdown-pill"
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        padding:      "2px 10px",
        borderRadius: 6,
        fontSize:     T.fsXs,
        fontWeight:   fw,
        background:   bg,
        color:        fg,
        whiteSpace:   "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// Category tag pill — coloured uppercase label identifying item type.
function CategoryTag({ type }) {
  const { label, bg, fg } = CATEGORY_STYLES[type] ?? CATEGORY_STYLES.keydate;
  return (
    <span
      data-testid="kdm-category-tag"
      style={{
        display:       "inline-block",
        fontSize:      T.fsXxxs5,
        fontWeight:    T.fwBold,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        padding:       "3px 8px",
        borderRadius:  6,
        textAlign:     "center",
        whiteSpace:    "nowrap",
        background:    bg,
        color:         fg,
        marginTop:     1,
      }}
    >
      {label}
    </span>
  );
}

// Single flat row — used for all item types.
// testid is type-specific so existing selector patterns keep working.
function FlatRow({ item, isFirst }) {
  const { _type, _dateLabel, _datePrefix, _subLine, _label, _dateStr, _granularity } = item;

  const testId =
    _type === "keydate"   ? "kdm-key-date-item"  :
    _type === "milestone" ? "kdm-milestone-item" :
                            "kdm-decision-item";

  return (
    <div
      data-testid={testId}
      style={{
        display:             "grid",
        gridTemplateColumns: "84px 1fr auto",
        gap:                 12,
        alignItems:          "start",
        padding:             "11px 0",
        ...(!isFirst && { borderTop: `1px solid ${HAIR_COLOR}` }),
        minWidth:            0,
      }}
    >
      {/* Category tag */}
      <CategoryTag type={_type} />

      {/* Description + optional sub-line */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize:     T.fsBase,
            fontWeight: T.fwSemibold,
            color:        T.dark,
            overflow:     "hidden",
            textOverflow: "ellipsis",
            whiteSpace:   "nowrap",
            lineHeight:   1.35,
          }}
        >
          {_label}
        </div>
        {_subLine && (
          <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>
            {_subLine}
          </div>
        )}
      </div>

      {/* Right: date + countdown pill */}
      <div
        style={{
          textAlign:  "right",
          whiteSpace: "nowrap",
        }}
      >
        <div style={{ fontSize: T.fsXs, color: T.mid }}>
          {_datePrefix}{_dateLabel}
        </div>
        <div style={{ marginTop: 4 }}>
          <CountdownPillEl dateStr={_dateStr} granularity={_granularity} />
        </div>
      </div>
    </div>
  );
}

// ─── DecisionsActionsCard ─────────────────────────────────────────────────────
//
// Export name kept as DecisionsActionsCard to minimise import churn.

export function DecisionsActionsCard({ location, activeLease, score, locationProjects, projectDecisions }) {
  // null  = not yet fetched (loading)
  // []    = fetched, zero milestones
  // [...] = fetched, real milestones
  const [fetchedMilestones, setFetchedMilestones] = useState(null);

  const activeProject = findActiveProject(location, locationProjects);

  // Self-fetch milestones for the active project whenever the project ID changes.
  useEffect(() => {
    if (!activeProject?.id) {
      setFetchedMilestones(null);
      return;
    }
    let cancelled = false;
    setFetchedMilestones(null);
    getProjectMilestones(activeProject.id)
      .then((list) => {
        if (!cancelled) {
          setFetchedMilestones(Array.isArray(list) ? list : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log.warn("DecisionsActionsCard: failed to fetch milestones", { err });
          setFetchedMilestones([]);
        }
      });
    return () => { cancelled = true; };
  }, [activeProject?.id]);

  // ── Build filtered section data ──────────────────────────────────────────

  // Lease key dates — derived then overdue-filtered
  const allKeyDates = deriveKeyDateItems(activeLease);
  const filteredKeyDates = allKeyDates.filter((item) => isWithinOverdueWindow(item.date));

  // Project milestones — sorted ascending by due date then overdue-filtered
  const rawMilestones = fetchedMilestones ?? [];
  const sortedMilestones = [...rawMilestones].sort((a, b) => {
    const da = parseDateStr(a.dueDate ?? a.due_date ?? null);
    const db = parseDateStr(b.dueDate ?? b.due_date ?? null);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });
  const filteredMilestones = activeProject
    ? sortedMilestones.filter((m) => isWithinOverdueWindow(m.dueDate ?? m.due_date ?? null))
    : [];

  // Decisions — project decisions (open, normalized) + synthetic renewal item,
  // sorted soonest-first (undated items last), then overdue-filtered.
  const normalizedProjectDecisions = Array.isArray(projectDecisions)
    ? projectDecisions
        .filter((d) => !d.status || d.status === "open")
        .map(normalizeProjectDecision)
        .filter(Boolean)
    : [];
  const allDecisions = [...normalizedProjectDecisions, ...deriveDecisionItems(activeLease, location)];
  const sortedDecisions = [...allDecisions].sort((a, b) => {
    const da = parseDateStr(a.targetDate);
    const db = parseDateStr(b.targetDate);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });
  const filteredDecisions = sortedDecisions.filter((d) => isWithinOverdueWindow(d.targetDate));

  // ── Merge into a single flat sorted array ────────────────────────────────

  const flatItems = [];

  for (const item of filteredKeyDates) {
    flatItems.push({
      _id:          item.id,
      _type:        "keydate",
      _sortDate:    parseDateStr(item.date),
      _label:       item.label,
      _subLine:     null,
      _dateLabel:   formatSpecificDate(item.date),
      _datePrefix:  "",
      _dateStr:     item.date,
      _granularity: "day",
    });
  }

  for (const m of filteredMilestones) {
    const dateField = m.dueDate ?? m.due_date ?? null;
    flatItems.push({
      _id:          m.id ?? `ms-${m.name ?? "unknown"}`,
      _type:        "milestone",
      _sortDate:    parseDateStr(dateField),
      _label:       m.name ?? m.title ?? "Milestone",
      _subLine:     m.owner ?? m.assignee ?? null,
      _dateLabel:   formatMonthYear(dateField),
      _datePrefix:  "",
      _dateStr:     dateField,
      _granularity: "month",
    });
  }

  for (const d of filteredDecisions) {
    flatItems.push({
      _id:          d.id,
      _type:        "decision",
      _sortDate:    parseDateStr(d.targetDate),
      _label:       d.title,
      _subLine:     d.owner ?? null,
      _dateLabel:   d.targetDate ? formatMonthYear(d.targetDate) : null,
      _datePrefix:  d.targetDate ? "by " : "",
      _dateStr:     d.targetDate,
      _granularity: "month",
    });
  }

  // Sort all items soonest-first; items with no parseable date go last.
  flatItems.sort((a, b) => {
    const da = a._sortDate, db = b._sortDate;
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da.getTime() - db.getTime();
  });

  // Build per-type sections preserving the global sort order within each type.
  // Then sort the sections themselves by their earliest item date so that e.g.
  // a milestone in July appears before a key-date in December.
  const SECTION_META = {
    keydate:   { testid: "kdm-section-dates",     key: "s-keydate"   },
    milestone: { testid: "kdm-section-milestones", key: "s-milestone" },
    decision:  { testid: "kdm-section-decisions",  key: "s-decision"  },
  };
  const sections = ["keydate", "milestone", "decision"]
    .map((type) => ({ type, items: flatItems.filter((i) => i._type === type) }))
    .filter((s) => s.items.length > 0)
    .sort((a, b) => {
      const da = a.items[0]._sortDate, db = b.items[0]._sortDate;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.getTime() - db.getTime();
    });

  const firstItemId = sections[0]?.items[0]?._id;

  return (
    <div
      data-testid="decisions-actions-card"
      style={{
        borderRadius:  10,
        padding:       "0 20px 16px",
        display:       "flex",
        flexDirection: "column",
        height:        "100%",
        boxSizing:     "border-box",
      }}
    >
      {/* Card header */}
      <div
        style={{
          display:    "flex",
          alignItems: "center",
          gap:        9,
          margin:     "0 -20px",
          padding:    "8px 20px",
          minHeight:  40,
        }}
      >
        <div
          style={{
            width:        3,
            height:       16,
            borderRadius: 2,
            background:   T.blue,
            flexShrink:   0,
          }}
        />
        <h3
          style={{
            margin:     0,
            fontSize:   T.fsBase,
            fontWeight: T.fwBold,
            color:      T.dark,
          }}
        >
          What&#39;s ahead
        </h3>
      </div>

      {/* Scrollable list body — fixed height (LOCATION-CARD-HEIGHT-LOCK-V2) */}
      <div
        data-testid="kdm-list-body"
        style={{
          flex:      1,
          minHeight: LIST_BODY_MIN_HEIGHT,
          maxHeight: LIST_BODY_MIN_HEIGHT,
          overflowY: "auto",
        }}
      >
        {flatItems.length === 0 ? (
          <div
            data-testid="kdm-empty-state"
            style={{
              fontSize: T.fsBase,
              color:    T.light,
              padding:  "16px 0",
            }}
          >
            Nothing scheduled.
          </div>
        ) : (
          sections.map((section) => (
            <div key={SECTION_META[section.type].key} data-testid={SECTION_META[section.type].testid}>
              {section.items.map((item) => (
                <FlatRow key={item._id} item={item} isFirst={item._id === firstItemId} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
